import { defineStore } from 'pinia'

import {
    getFilters,
    updateFilters,
    deleteFilters,
    deleteAllFilters,
} from '@/api/call-tracking/campaign/campaign-rtb-filter-routes'

import { checkEqualsArrays, copyArray, getRandomUUID } from '@/helpers/app-helper'

import { useDetailCampaignStore } from '@/store/call-tracking/campaigns/detailCampaignStore'
import { useDefaultStore } from '@/store/defaultStore'
import { useDetailCampaignRtbParametersStore } from '@/store/call-tracking/campaigns/detailCampaignRtbParametersStore'
import { useDetailCampaignRtbStore } from '@/store/call-tracking/campaigns/detailCampaignRtbStore'

export const useDetailCampaignRtbFiltersStore = defineStore('detailCampaignRtbFilters', {
    state: () => ({
        createDataLoading: false,
        updateLoading: false,

        items: [],
        copyItems: [],
        removeItems: [],

        enabledSave: true,
        enableValidate: false,
        showErrorInDialog: false,
        errorMaxLengths: 0,
    }),

    getters: {
        campaignSlug() {
            const detailCampaignStore = useDetailCampaignStore()
            return detailCampaignStore.slug
        },

        itemsWithoutRemove(state) {
            return state.items.filter((item) => !state.removeItems.some((removedItem) => {
                const matchById = removedItem.id && removedItem.id === item.id
                const matchByNewId = removedItem.newId && removedItem.newId === item.newId
                return matchById || matchByNewId
            }))
        },

        noModify(state) {
            return checkEqualsArrays(this.itemsWithoutRemove, state.copyItems)
        },

        activateOverrideFirstTime() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.activeOverrideFirstTime
        },

        numberId() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.formData.number_id
        },

        vendorId() {
            const detailCampaignRtbStore = useDetailCampaignRtbStore()

            return detailCampaignRtbStore.formData.vendor_id
        },
    },

    actions: {
        checkValueForErrorMaxLength() {
            this.resetErrorMaxLengths()

            this.itemsWithoutRemove.forEach((item) => {
                if (item.values && item.values.length > 255) {
                    this.errorMaxLengths += 1
                }
            })
        },

        resetErrorMaxLengths() {
            this.errorMaxLengths = 0
        },

        changeEnabledSave(enabledSave) {
            this.enabledSave = enabledSave
        },

        changeValidate(enableValidate) {
            this.enableValidate = enableValidate
        },

        changeValidateBySave() {
            this.changeValidate(true)

            setTimeout(() => {
                this.changeValidate(false)
            }, 0)
        },

        changeErrorInDialog(showErrorInDialog) {
            this.showErrorInDialog = showErrorInDialog
        },

        returnToPreviousChanges() {
            this.items = copyArray(this.copyItems)
            this.clearRemoveItems()
        },

        updateFiltersAfterRemoveParameter(id) {
            const items = [...this.items].filter((item) => item.parameter_id !== id)
            this.clearFilters()
            this.parsePayloadFilters(items)
        },

        removeFilter(removeItem) {
            const { newId } = removeItem

            if (newId) {
                this.items = this.items.filter((item) => item.newId !== newId)
            }

            this.removeItems.push(removeItem)
        },

        updateFilter(newFilter) {
            const findIndex = this.items.findIndex((item) => (item.id && item.id === newFilter.id) || (item.newId && item.newId === newFilter.newId))

            if (findIndex !== -1) {
                this.items.splice(findIndex, 1, newFilter)
            }
        },

        addFilters() {
            const newFilter = {
                parameter_id: null,
                operator_name: null,
                operator_key: null,
                file_name: null,
                values: null,
                newId: getRandomUUID(),
            }

            const newItems = [...this.items, newFilter]

            if (this.items.length) {
                this.items = newItems
            } else {
                this.setItems(newItems)
            }
        },

        clearFilters() {
            this.items = []
            this.copyItems = []
        },

        clearRemoveItems() {
            this.removeItems = []
        },

        setItems(payload) {
            this.items = payload
            this.copyItems = copyArray(payload)
        },

        findDuplicate(isDuplicate) {
            if (!isDuplicate) {
                return false
            }

            const detailCampaignRtbParametersStore = useDetailCampaignRtbParametersStore()
            return detailCampaignRtbParametersStore.findDuplicate(this.itemsWithoutRemove)
        },

        parsePayloadFilters(payload) {
            if (payload.length) {
                this.setItems(payload)
            } else {
                this.addFilters()
                this.setItems(this.items)
            }
        },

        async goToUpdateFilters(saveAsNew = false, hideMessage = false) {
            const defaultStore = useDefaultStore()

            if (!this.itemsWithoutRemove.length) {
                this.clearRemoveItems()
                return { success: true }
            }

            this.updateLoading = true

            const conditions = this.itemsWithoutRemove.map((item, index) => ({
                ...item,
                id: saveAsNew ? null : item.id,
                newId: saveAsNew ? getRandomUUID() : null,
                order: index + 1,
            }))

            const { success, payload, message } = await updateFilters({
                slug: this.campaignSlug,
                formData: {
                    number_id: this.numberId,
                    vendor_id: this.vendorId,
                    conditions,
                },
            })

            if (success) {
                this.clearRemoveItems()
                this.parsePayloadFilters(payload)

                if (!hideMessage) {
                    defaultStore.setSuccessMessage({ message })
                }
            } else {
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success }
        },

        async goToDeleteFilters() {
            const items = this.removeItems.filter((item) => !item.newId)

            if (!items.length) {
                return { success: true }
            }

            this.updateLoading = true

            const { success, message } = await deleteFilters({
                slug: this.campaignSlug,
                data: {
                    number_id: this.numberId,
                    ids: items.map((item) => item.id),
                },
            })

            if (!success) {
                const defaultStore = useDefaultStore()
                defaultStore.setErrorMessage({ message })
            }

            this.updateLoading = false

            return { success, message }
        },

        async goToDeleteAllFilters() {
            const defaultStore = useDefaultStore()

            const { success, message } = await deleteAllFilters({
                slug: this.campaignSlug,
                data: {
                    number_id: this.numberId,
                },
            })

            if (success) {
                this.clearRemoveItems()
                this.clearFilters()
                this.parsePayloadFilters([])
                defaultStore.setSuccessMessage({ message })
            } else {
                defaultStore.setErrorMessage({ message })
            }

            return { success }
        },

        async goToLoadFilters(isDuplicate = false) {
            const defaultStore = useDefaultStore()

            const isDuplicateFound = this.findDuplicate(isDuplicate)

            const { success, payload, message } = await getFilters({
                slug: this.campaignSlug,
                params: {
                    number_id: this.activateOverrideFirstTime ? null : this.numberId,
                },
            })

            if (success) {
                if (isDuplicateFound) {
                    defaultStore.setErrorMessage({ message: 'Any condition with the “Is duplicate” operator has been deleted' })
                }

                this.setItems([])
                this.parsePayloadFilters(payload)
            } else {
                defaultStore.setErrorMessage({ message })
            }

            return { success, payload }
        },
    },
})
